import { useState, useEffect } from 'react';
import { Select, MenuItem, FormControl, SelectChangeEvent, Chip, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { setSession } from '../../../utils/jwt';
import Logo from '../../shared/Logo';

interface MultiAccountToken {
  account_id: string;
  account_name: string;
  account_type: string;
  token: string;
  user_email: string;
  user_id: string;
}

export default function AccountSwitcher({ hideWhenEmpty = false }: { hideWhenEmpty?: boolean }) {
  const [accounts, setAccounts] = useState<MultiAccountToken[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');

  useEffect(() => {
    const multiAccountTokensStr = localStorage.getItem('multi_account_tokens');
    
    if (!multiAccountTokensStr) {
      return;
    }

    try {
      const parsedAccounts = JSON.parse(multiAccountTokensStr) as MultiAccountToken[];
      
      if (Array.isArray(parsedAccounts) && parsedAccounts.length > 0) {
        setAccounts(parsedAccounts);
        // Set the current account based on the current accessToken
        const currentToken = localStorage.getItem('accessToken');
        const currentAccount = parsedAccounts.find(acc => acc.token === currentToken);
        if (currentAccount) {
          setSelectedAccountId(currentAccount.account_id);
        } else if (parsedAccounts.length > 0) {
          // Default to first account if current token doesn't match
          setSelectedAccountId(parsedAccounts[0].account_id);
        }
      }
    } catch (error) {
      console.error('Error parsing multi_account_tokens:', error);
    }
  }, []);

  const handleAccountChange = (event: SelectChangeEvent<string>) => {
    const accountId = event.target.value;
    const selectedAccount = accounts.find(acc => acc.account_id === accountId);
    
    if (selectedAccount) {
      setSelectedAccountId(accountId);
      setSession(selectedAccount.token);
      // Refresh the page to apply the new token
      window.location.reload();
    }
  };

  // Return null if no accounts are available
  if (accounts.length === 0) {
    if (hideWhenEmpty) return null;

    return (
      <Box component={RouterLink} to="/" sx={{ display: 'inline-flex' }}>
        <Logo />
      </Box>
    );
  }

  return (
    <FormControl size="small" sx={{ width: 225 }}>
      <Select
        value={selectedAccountId}
        onChange={handleAccountChange}
        displayEmpty
        size="small"
        renderValue={(value) => {
          const account = accounts.find(acc => acc.account_id === value);
          if (!account) return '';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box>{account.account_name}</Box>
              <Chip
                label={account.account_type === 'main' ? 'Main' : 'Sub'}
                size="small"
                color={account.account_type === 'main' ? 'primary' : 'default'}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
          );
        }}
      >
        {accounts.map((account) => (
          <MenuItem key={account.account_id} value={account.account_id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box sx={{ flex: 1 }}>{account.account_name}</Box>
              <Chip
                label={account.account_type === 'main' ? 'Main' : 'Sub'}
                size="small"
                color={account.account_type === 'main' ? 'primary' : 'default'}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

